import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

function InvoiceGenerator({ user }) {
    const [data, setData] = useState({
        fees: [],
        students: [],
        accountsReceivable: []
    });

    const [invoiceConfig, setInvoiceConfig] = useState({
        parent: '',
        dateRange: 'month', // month, custom, ytd
        month: '',
        fromDate: '',
        toDate: '',
        includePayments: true,
        includeLogo: false,
        notes: 'Please send payment within 7 days to apollo.academy1827@gmail.com. Thank you!'
    });

    const [isGenerating, setIsGenerating] = useState(false);
    const [preview, setPreview] = useState(null);

    const now = new Date();

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [feesRes, studentsRes, arRes] = await Promise.all([
                    axiosInstance.get('/logs'),
                    axiosInstance.get('/students'),
                    axiosInstance.get('/receivables')
                ]);

                setData({
                    fees: feesRes.data,
                    students: studentsRes.data,
                    accountsReceivable: arRes.data
                });
            } catch (err) {
                console.error('Failed to fetch data:', err);
                alert('Failed to load data');
            }
        };

        fetchData();
    }, []);

    // Set default month to current month
    useEffect(() => {
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setInvoiceConfig(prev => ({ ...prev, month }));
    }, []);

    // Helper functions
    const parseLocalDate = (dateStr) => {
        if (!dateStr) return new Date();
        if (dateStr.includes('T')) return new Date(dateStr);
        return new Date(dateStr + 'T12:00:00');
    };

    const getStudentByName = (name) => data.students.find(s => s.name === name);

    const getStudentRateOnDate = (student, sessionDate) => {
        if (!student) return 0;
        const date = new Date(sessionDate);

        if (!student.rateHistory || student.rateHistory.length === 0) {
            return student.rate;
        }

        for (let i = student.rateHistory.length - 1; i >= 0; i--) {
            const historyEntry = student.rateHistory[i];
            const effectiveDate = new Date(historyEntry.effectiveDate);
            const endDate = historyEntry.endDate ? new Date(historyEntry.endDate) : new Date('2099-12-31');

            if (date >= effectiveDate && date <= endDate) {
                return historyEntry.rate;
            }
        }

        return student.rate;
    };

    // Get unique parents
    const uniqueParents = [...new Set(data.students.map(s => s.parent))].sort();

    // Filter sessions based on config
    const getFilteredSessions = () => {
        if (!invoiceConfig.parent) return [];

        let filtered = data.fees.filter(f => {
            const student = getStudentByName(f.student);
            return student?.parent === invoiceConfig.parent;
        });

        // Apply date filters
        if (invoiceConfig.dateRange === 'month' && invoiceConfig.month) {
            const [year, month] = invoiceConfig.month.split('-').map(Number);
            filtered = filtered.filter(f => {
                const d = parseLocalDate(f.date);
                return d.getFullYear() === year && d.getMonth() + 1 === month;
            });
        } else if (invoiceConfig.dateRange === 'custom') {
            if (invoiceConfig.fromDate) {
                filtered = filtered.filter(f => parseLocalDate(f.date) >= parseLocalDate(invoiceConfig.fromDate));
            }
            if (invoiceConfig.toDate) {
                filtered = filtered.filter(f => parseLocalDate(f.date) <= parseLocalDate(invoiceConfig.toDate));
            }
        } else if (invoiceConfig.dateRange === 'ytd') {
            const currentYear = now.getFullYear();
            filtered = filtered.filter(f => parseLocalDate(f.date).getFullYear() === currentYear);
        }

        return filtered.sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
    };

    // Calculate totals
    const calculateInvoiceData = () => {
        const sessions = getFilteredSessions();

        const sessionDetails = sessions.map(f => {
            const student = getStudentByName(f.student);
            const rate = getStudentRateOnDate(student, f.date);
            return {
                date: parseLocalDate(f.date),
                student: f.student,
                hours: f.hours,
                rate: rate,
                amount: f.hours * rate
            };
        });

        const totalBilled = sessionDetails.reduce((sum, s) => sum + s.amount, 0);
        const totalHours = sessionDetails.reduce((sum, s) => sum + s.hours, 0);

        // Calculate payments if needed
        let totalPaid = 0;
        if (invoiceConfig.includePayments) {
            const payments = data.accountsReceivable.filter(ar => {
                if (ar.parent !== invoiceConfig.parent) return false;
                if (ar.amount >= 0) return false; // Only count payments (negative amounts)

                const arDate = parseLocalDate(ar.date);

                if (invoiceConfig.dateRange === 'month' && invoiceConfig.month) {
                    const [year, month] = invoiceConfig.month.split('-').map(Number);
                    return arDate.getFullYear() === year && arDate.getMonth() + 1 === month;
                } else if (invoiceConfig.dateRange === 'custom') {
                    let inRange = true;
                    if (invoiceConfig.fromDate) {
                        inRange = inRange && arDate >= parseLocalDate(invoiceConfig.fromDate);
                    }
                    if (invoiceConfig.toDate) {
                        inRange = inRange && arDate <= parseLocalDate(invoiceConfig.toDate);
                    }
                    return inRange;
                } else if (invoiceConfig.dateRange === 'ytd') {
                    return arDate.getFullYear() === now.getFullYear();
                }
                return true;
            });

            totalPaid = Math.abs(payments.reduce((sum, p) => sum + p.amount, 0));
        }

        return {
            sessionDetails,
            totalBilled,
            totalHours,
            totalPaid,
            balance: totalBilled - totalPaid
        };
    };

    // Generate preview
    const handlePreview = () => {
        if (!invoiceConfig.parent) {
            alert('Please select a parent');
            return;
        }

        const invoiceData = calculateInvoiceData();

        if (invoiceData.sessionDetails.length === 0) {
            alert('No sessions found for the selected criteria');
            return;
        }

        setPreview(invoiceData);
    };

    // Generate PDF Invoice
    const generateInvoice = () => {
        if (!invoiceConfig.parent) {
            alert('Please select a parent');
            return;
        }

        setIsGenerating(true);

        try {
            const invoiceData = calculateInvoiceData();

            if (invoiceData.sessionDetails.length === 0) {
                alert('No sessions found for the selected criteria');
                setIsGenerating(false);
                return;
            }

            const doc = new jsPDF();

            // Header
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.text('Apollo Academy', 14, 20);

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('Invoice', 14, 28);

            // Invoice details
            doc.setFontSize(10);
            doc.text(`Invoice Date: ${new Date().toLocaleDateString()}`, 140, 20);
            doc.text(`Invoice #: INV-${Date.now()}`, 140, 26);

            // Bill To
            doc.setFont('helvetica', 'bold');
            doc.text('Bill To:', 14, 40);
            doc.setFont('helvetica', 'normal');
            doc.text(invoiceConfig.parent, 14, 46);

            // Period
            let periodText = '';
            if (invoiceConfig.dateRange === 'month' && invoiceConfig.month) {
                const [year, month] = invoiceConfig.month.split('-');
                const monthName = new Date(year, month - 1).toLocaleString('en-US', { month: 'long' });
                periodText = `${monthName} ${year}`;
            } else if (invoiceConfig.dateRange === 'custom') {
                periodText = `${invoiceConfig.fromDate || 'Start'} to ${invoiceConfig.toDate || 'End'}`;
            } else if (invoiceConfig.dateRange === 'ytd') {
                periodText = `Year to Date ${now.getFullYear()}`;
            }
            doc.text(`Period: ${periodText}`, 14, 52);

            // Sessions table
            const tableData = invoiceData.sessionDetails.map(s => [
                s.date.toLocaleDateString(),
                s.student,
                s.hours.toFixed(2),
                `$${s.rate.toFixed(2)}`,
                `$${s.amount.toFixed(2)}`
            ]);

            autoTable(doc, {
                startY: 60,
                head: [['Date', 'Student', 'Hours', 'Rate', 'Amount']],
                body: tableData,
                theme: 'grid',
                headStyles: {
                    fillColor: [26, 42, 108],
                    fontStyle: 'bold',
                    textColor: [255, 255, 255]
                },
                styles: {
                    fontSize: 9,
                    cellPadding: 3
                },
                columnStyles: {
                    2: { halign: 'right' },
                    3: { halign: 'right' },
                    4: { halign: 'right' }
                }
            });

            // Summary
            const finalY = doc.lastAutoTable.finalY + 10;

            doc.setDrawColor(200, 200, 200);
            doc.line(120, finalY, 195, finalY);

            doc.setFont('helvetica', 'bold');
            doc.text('Total Hours:', 120, finalY + 7);
            doc.text(invoiceData.totalHours.toFixed(2), 195, finalY + 7, { align: 'right' });

            doc.text('Total Billed:', 120, finalY + 14);
            doc.text(`$${invoiceData.totalBilled.toFixed(2)}`, 195, finalY + 14, { align: 'right' });

            if (invoiceConfig.includePayments && invoiceData.totalPaid > 0) {
                doc.setFont('helvetica', 'normal');
                doc.text('Payments Received:', 120, finalY + 21);
                doc.text(`-$${invoiceData.totalPaid.toFixed(2)}`, 195, finalY + 21, { align: 'right' });

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.text('Balance Due:', 120, finalY + 30);
                doc.text(`$${invoiceData.balance.toFixed(2)}`, 195, finalY + 30, { align: 'right' });
            }

            // Payment Instructions - FIXED VERSION
            if (invoiceConfig.notes && invoiceConfig.notes.trim() !== '') {
                const notesStartY = finalY + 45;

                // Add separator line
                doc.setDrawColor(200, 200, 200);
                doc.line(14, notesStartY, 195, notesStartY);

                // Notes header
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(0, 0, 0);
                doc.text('Payment Instructions:', 14, notesStartY + 8);

                // Notes content
                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                const splitNotes = doc.splitTextToSize(invoiceConfig.notes, 180);
                doc.text(splitNotes, 14, notesStartY + 15);
            }

            // Footer
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text('Thank you for your business!', 105, 280, { align: 'center' });

            // Save
            const filename = `Invoice_${invoiceConfig.parent.replace(/\s+/g, '_')}_${periodText.replace(/\s+/g, '_')}.pdf`;
            doc.save(filename);

            alert('Invoice generated successfully!');
        } catch (err) {
            console.error('Invoice generation failed:', err);
            alert('Failed to generate invoice. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="page-container">
            <header className="content-header">
                <h2>Invoice Generator</h2>
                <p>Generate professional invoices for parents</p>
            </header>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                {/* Configuration Panel */}
                <div style={{
                    backgroundColor: 'var(--card-bg)',
                    padding: '30px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Invoice Configuration</h3>

                    <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                            Parent *
                        </label>
                        <select
                            value={invoiceConfig.parent}
                            onChange={e => setInvoiceConfig({ ...invoiceConfig, parent: e.target.value })}
                        >
                            <option value="">Select Parent</option>
                            {uniqueParents.map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                            Date Range
                        </label>
                        <select
                            value={invoiceConfig.dateRange}
                            onChange={e => setInvoiceConfig({ ...invoiceConfig, dateRange: e.target.value })}
                        >
                            <option value="month">Specific Month</option>
                            <option value="ytd">Year to Date</option>
                            <option value="custom">Custom Range</option>
                        </select>
                    </div>

                    {invoiceConfig.dateRange === 'month' && (
                        <div className="form-group">
                            <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                Month
                            </label>
                            <input
                                type="month"
                                value={invoiceConfig.month}
                                onChange={e => setInvoiceConfig({ ...invoiceConfig, month: e.target.value })}
                            />
                        </div>
                    )}

                    {invoiceConfig.dateRange === 'custom' && (
                        <>
                            <div className="form-group">
                                <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    value={invoiceConfig.fromDate}
                                    onChange={e => setInvoiceConfig({ ...invoiceConfig, fromDate: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    value={invoiceConfig.toDate}
                                    onChange={e => setInvoiceConfig({ ...invoiceConfig, toDate: e.target.value })}
                                />
                            </div>
                        </>
                    )}

                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={invoiceConfig.includePayments}
                                onChange={e => setInvoiceConfig({ ...invoiceConfig, includePayments: e.target.checked })}
                            />
                            Include payments and show balance due
                        </label>
                    </div>

                    <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                            Payment Instructions
                        </label>
                        <textarea
                            value={invoiceConfig.notes}
                            onChange={e => setInvoiceConfig({ ...invoiceConfig, notes: e.target.value })}
                            rows={4}
                            placeholder="Add payment instructions..."
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                border: '1px solid var(--input-border)',
                                fontFamily: 'inherit',
                                fontSize: '0.9rem',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
                        <button
                            className="submit-btn"
                            onClick={handlePreview}
                            style={{ flex: 1 }}
                        >
                            Preview
                        </button>
                        <button
                            className="add-btn"
                            onClick={generateInvoice}
                            disabled={isGenerating}
                            style={{ flex: 1 }}
                        >
                            {isGenerating ? 'Generating...' : 'Generate Invoice'}
                        </button>
                    </div>
                </div>

                {/* Preview Panel */}
                <div style={{
                    backgroundColor: 'var(--card-bg)',
                    padding: '30px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Preview</h3>

                    {!preview ? (
                        <div style={{
                            textAlign: 'center',
                            color: '#999',
                            padding: '60px 20px',
                            fontSize: '0.95rem'
                        }}>
                            <p>Configure invoice settings and click "Preview" to see a summary</p>
                        </div>
                    ) : (
                        <div>
                            <div style={{
                                borderBottom: '2px solid var(--primary)',
                                paddingBottom: '15px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ margin: 0, fontSize: '1.3rem' }}>Apollo Academy</h4>
                                <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#666' }}>Invoice Preview</p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '5px 0', fontSize: '0.95rem' }}>
                                    <strong>Bill To:</strong> {invoiceConfig.parent}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '0.9rem', color: '#666' }}>
                                    Date: {new Date().toLocaleDateString()}
                                </p>
                            </div>

                            <div style={{
                                backgroundColor: '#f9fafb',
                                padding: '15px',
                                borderRadius: '8px',
                                marginBottom: '20px'
                            }}>
                                <table style={{ width: '100%', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                            <th style={{ textAlign: 'left', padding: '8px 4px' }}>Date</th>
                                            <th style={{ textAlign: 'left', padding: '8px 4px' }}>Student</th>
                                            <th style={{ textAlign: 'right', padding: '8px 4px' }}>Hours</th>
                                            <th style={{ textAlign: 'right', padding: '8px 4px' }}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.sessionDetails.slice(0, 5).map((s, idx) => (
                                            <tr key={idx}>
                                                <td style={{ padding: '6px 4px' }}>{s.date.toLocaleDateString()}</td>
                                                <td style={{ padding: '6px 4px' }}>{s.student}</td>
                                                <td style={{ textAlign: 'right', padding: '6px 4px' }}>{s.hours.toFixed(2)}</td>
                                                <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                                    ${s.amount.toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {preview.sessionDetails.length > 5 && (
                                    <p style={{
                                        textAlign: 'center',
                                        fontSize: '0.8rem',
                                        color: '#666',
                                        margin: '10px 0 0 0'
                                    }}>
                                        ... and {preview.sessionDetails.length - 5} more sessions
                                    </p>
                                )}
                            </div>

                            <div style={{
                                borderTop: '2px solid #e5e7eb',
                                paddingTop: '15px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '8px'
                                }}>
                                    <span style={{ fontWeight: 600 }}>Total Hours:</span>
                                    <span>{preview.totalHours.toFixed(2)}</span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '8px'
                                }}>
                                    <span style={{ fontWeight: 600 }}>Total Billed:</span>
                                    <span>${preview.totalBilled.toFixed(2)}</span>
                                </div>

                                {invoiceConfig.includePayments && preview.totalPaid > 0 && (
                                    <>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '8px',
                                            color: '#10b981'
                                        }}>
                                            <span>Payments Received:</span>
                                            <span>-${preview.totalPaid.toFixed(2)}</span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            marginTop: '12px',
                                            paddingTop: '12px',
                                            borderTop: '1px solid #e5e7eb',
                                            color: preview.balance > 0 ? '#ef4444' : '#10b981'
                                        }}>
                                            <span>Balance Due:</span>
                                            <span>${preview.balance.toFixed(2)}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default InvoiceGenerator;